import React, {useEffect, useState} from "react"
import FavoriteIcon from "@mui/icons-material/Favorite"
import ShareIcon from "@mui/icons-material/Share"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk"
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact"
import {getRecorridosProperties} from "@/api/properties"
import {getPropertyViews} from "@/api/ga4"
import {getPathUrl, type PropertyScheme} from "@/utils"
import type {RegionCode} from "@casas-diamante/types"

export type PropertyIndicatorsProperty = {
   id?: string
   type?: string
   forRent?: boolean
   shares?: number
   likes?: number
   interests?: number
   region?: string
   slug?: string
}

const PLACEHOLDER = "—"

const isFiniteNumber = (value: unknown): value is number =>
   typeof value === "number" && Number.isFinite(value)

const formatNumber = (value: unknown) =>
   isFiniteNumber(value) ? value.toLocaleString("es-MX") : PLACEHOLDER

const formatPercent = (value: unknown) =>
   isFiniteNumber(value)
      ? `${value.toLocaleString(undefined, {
           minimumFractionDigits: 2,
           maximumFractionDigits: 2,
        })}%`
      : PLACEHOLDER

const getCurrentScheme = (property: PropertyIndicatorsProperty, scheme: string): PropertyScheme =>
   scheme === "rent" ? "rent" : scheme === "sale" ? "sale" : property?.forRent ? "rent" : "sale"

const extractRecorridosCount = (value: unknown) => {
   if (!value || typeof value !== "object") return 0
   const payload = value as {total?: unknown; recorridos?: unknown; data?: unknown}
   if (isFiniteNumber(payload.total)) return payload.total
   if (Array.isArray(payload.recorridos)) return payload.recorridos.length
   if (payload.data && typeof payload.data === "object") {
      const data = payload.data as {total?: unknown; recorridos?: unknown}
      if (isFiniteNumber(data.total)) return data.total
      if (Array.isArray(data.recorridos)) return data.recorridos.length
   }
   return 0
}

function PropertyIndicatorsTable({
   property,
   scheme,
}: {
   property: PropertyIndicatorsProperty
   scheme: string
}) {
   const [views, setViews] = useState<number | null>(null)
   const [recorridosCount, setRecorridosCount] = useState<number | null>(null)
   const currentScheme = getCurrentScheme(property, scheme)

   useEffect(() => {
      let isMounted = true
      if (!property.region || !property.type || !property.slug) return
      const path = getPathUrl(
         property.region as RegionCode,
         property.type,
         currentScheme,
         property.slug,
      )

      getPropertyViews(path)
         .then((count) => {
            if (isMounted) setViews(count)
         })
         .catch(() => {
            if (isMounted) setViews(null)
         })

      if (property.id) {
         getRecorridosProperties(property.id)
            .then((response) => {
               if (isMounted) setRecorridosCount(extractRecorridosCount(response))
            })
            .catch(() => {
               if (isMounted) setRecorridosCount(null)
            })
      } else {
         setRecorridosCount(null)
      }

      return () => {
         isMounted = false
      }
   }, [currentScheme, property.id, property.region, property.slug, property.type])

   const getConversionRate = (denominator: unknown) => {
      if (
         !isFiniteNumber(denominator) ||
         denominator <= 0 ||
         !isFiniteNumber(recorridosCount) ||
         recorridosCount < 0
      ) {
         return null
      }
      return (recorridosCount / denominator) * 100
   }

   const rows = [
      {
         key: "views",
         label: "Visitas al sitio web",
         value: views,
         rate: getConversionRate(views),
         note: "% de Visitas en recorridos",
         icon: <VisibilityIcon className="text-blue" fontSize="small" />,
      },
      {
         key: "shares",
         label: "Veces compartido",
         value: property.shares,
         rate: getConversionRate(property.shares),
         note: "% de compartidos en recorridos",
         icon: <ShareIcon className="text-green" fontSize="small" />,
      },
      {
         key: "likes",
         label: "Me gusta",
         value: property.likes,
         rate: getConversionRate(property.likes),
         note: "% de me gusta en recorridos",
         icon: <FavoriteIcon className="text-green" fontSize="small" />,
      },
      {
         key: "interests",
         label: "Interesados",
         value: property.interests,
         rate: getConversionRate(property.interests),
         note: "% de interesados en recorridos",
         icon: <ConnectWithoutContactIcon className="text-purple-600" fontSize="small" />,
      },
      {
         key: "recorridos",
         label: "Recorridos realizados",
         value: recorridosCount,
         rate: null,
         note: "Base para los porcentajes",
         icon: <DirectionsWalkIcon className="text-orange-500" fontSize="small" />,
      },
   ]

   return (
      <div>
         <h3 className="mb-1 font-bold text-blue">Indicadores del Inmueble</h3>
         <div className="w-full">
            <table className="w-full table-fixed border-collapse text-xs text-gray-500 sm:text-sm">
               <colgroup>
                  <col className="w-[47%]" />
                  <col className="w-[18%]" />
                  <col className="w-[35%]" />
               </colgroup>
               <thead>
                  <tr className="bg-blue text-white">
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Indicador
                     </th>
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Valor
                     </th>
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Conversión
                     </th>
                  </tr>
               </thead>
               <tbody>
                  {rows.map((row) => (
                     <tr key={row.key} className="bg-white">
                        <td className="border border-gray-200 px-1 py-1 sm:px-2 sm:py-2">
                           <div className="flex items-start gap-1 sm:items-center sm:gap-2">
                              {row.icon}
                              <span className="break-words font-normal text-blue">{row.label}</span>
                           </div>
                        </td>
                        <td className="break-words border border-gray-200 px-1 py-1 text-center text-sm font-normal sm:px-2 sm:py-2 sm:text-lg">
                           {formatNumber(row.value)}
                        </td>
                        <td className="break-words border border-gray-200 px-1 py-1 text-center sm:px-2 sm:py-2">
                           <span className="block text-sm font-normal text-green sm:text-lg">
                              {formatPercent(row.rate)}
                           </span>
                           <span className="text-[11px] leading-tight text-gray-500 sm:text-xs">
                              {row.note}
                           </span>
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </div>
      </div>
   )
}

export default PropertyIndicatorsTable
